import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Check, Loader2, ShieldCheck, LogIn, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, BUSINESS } from "@/lib/api";
import { windowsFor, isDayOpen, isMaricopa, MAX_PER_SLOT } from "@/lib/schedule";
import { useAuth } from "@/contexts/AuthContext";

const SERVICES = [
  "Dry Cleaning",
  "Laundry / Wash & Fold",
  "Alterations",
  "Comforters / Bedding",
  "Leather / Suede",
  "Wedding Gowns",
];

const FABRIC_BG =
  "https://static.prod-images.emergentagent.com/jobs/8271852f-8769-4308-a435-1104bfab838c/images/8037e1876a6becb01c642a7f16c1440598bc5353997380784b96e0d4a67e7ef7.png";

const LOW_STOCK_THRESHOLD = 3;

function windowPlaceholder(hasDate, hasWindows) {
  if (!hasDate) return "Pick a date first";
  if (!hasWindows) return "Closed that day";
  return "Choose a time window";
}

function capacityColor(full, remaining) {
  if (full) return "text-red-600";
  if (remaining <= LOW_STOCK_THRESHOLD) return "text-amber-600";
  return "text-emerald-600";
}

export default function Booking() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: BUSINESS.serviceAreas[0],
    services: ["Dry Cleaning"],
    pickup_date: null,
    pickup_window: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [availability, setAvailability] = useState({}); // { window: {remaining,full} }

  // Pre-fill form from logged-in user's profile
  useEffect(() => {
    if (user) {
      setForm((p) => ({
        ...p,
        name: p.name || user.name || "",
        phone: p.phone || user.phone || "",
        email: p.email || user.email || "",
        address: p.address || user.address || "",
        city: p.city || user.city || BUSINESS.serviceAreas[0],
      }));
    }
  }, [user]);

  const availableWindows = useMemo(
    () => windowsFor(form.city, form.pickup_date),
    [form.city, form.pickup_date]
  );

  // Whenever city changes, clear the date+window if no longer valid
  useEffect(() => {
    if (form.pickup_date && !isDayOpen(form.city, form.pickup_date)) {
      setForm((p) => ({ ...p, pickup_date: null, pickup_window: "" }));
      setAvailability({});
    }
  }, [form.city, form.pickup_date]);

  // Fetch live availability whenever city + date are set
  useEffect(() => {
    let cancelled = false;
    if (!form.pickup_date || availableWindows.length === 0) {
      setAvailability({});
      return;
    }
    const dateStr = format(form.pickup_date, "yyyy-MM-dd");
    api
      .get(`/availability?city=${encodeURIComponent(form.city)}&date=${dateStr}`)
      .then(({ data }) => {
        if (cancelled) return;
        setAvailability(data.windows || {});
        // Auto-select first non-full window if current is empty/invalid/full
        const current = form.pickup_window;
        const currentInfo = data.windows?.[current];
        if (!current || !currentInfo || currentInfo.full) {
          const firstOpen = availableWindows.find(
            (w) => !data.windows?.[w]?.full
          );
          if (firstOpen) setForm((p) => ({ ...p, pickup_window: firstOpen }));
        }
      })
      .catch(() => {
        if (!cancelled) setAvailability({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.city, form.pickup_date]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleService = (s) => {
    setForm((p) => ({
      ...p,
      services: p.services.includes(s)
        ? p.services.filter((x) => x !== s)
        : [...p.services, s],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.pickup_date) {
      toast.error("Please fill in name, phone, address and pickup date.");
      return;
    }
    if (!form.pickup_window) {
      toast.error("Please choose a pickup time window.");
      return;
    }
    if (form.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        pickup_date: format(form.pickup_date, "yyyy-MM-dd"),
        email: form.email || null,
      };
      await api.post("/pickup-requests", payload);
      setSuccess(true);
      toast.success("Pickup request received! We'll call to confirm shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === "string"
          ? msg
          : "Something went wrong. Please call us at " + BUSINESS.phone
      );
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <section id="schedule" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-brand-blue mx-auto" />
        </div>
      </section>
    );
  }

  if (!user) {
    return (
      <section id="schedule" data-testid="booking-gate-signin" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-brand-blue/10 text-brand-blue items-center justify-center mb-5">
            <LogIn className="h-6 w-6" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy">
            Sign in to schedule a pickup
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-xl mx-auto">
            Create a quick account so we can save your address, your card on file, and your service history.
            We only charge your card <strong>after</strong> we process your items in-store — never before pickup.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full bg-brand-blue hover:bg-brand-navy text-white h-12 px-7 font-semibold" data-testid="gate-signup-button">
              <Link to="/signup?next=/profile">Create account</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-7 font-semibold" data-testid="gate-login-button">
              <Link to="/login?next=/profile">Sign in</Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (!user.has_card) {
    return (
      <section id="schedule" data-testid="booking-gate-card" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-amber-100 text-amber-700 items-center justify-center mb-5">
            <CreditCard className="h-6 w-6" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy">
            Add a card before you schedule
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-xl mx-auto">
            We securely save your card via Stripe so we can charge you after processing your items.
            Your card details never touch our servers.
          </p>
          <Button asChild className="mt-7 rounded-full bg-brand-blue hover:bg-brand-navy text-white h-12 px-7 font-semibold" data-testid="gate-add-card-button">
            <Link to="/profile">Add card on file</Link>
          </Button>
        </div>
      </section>
    );
  }

  if (success) {
    return (
      <section id="schedule" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex h-16 w-16 rounded-full bg-brand-blue text-white items-center justify-center mb-6">
            <Check className="h-7 w-7" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy">
            Pickup scheduled — see you soon!
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Thanks, {form.name.split(" ")[0]}. We received your request for
            pickup on{" "}
            <strong>{form.pickup_date && format(form.pickup_date, "EEEE, MMM d")}</strong>{" "}
            during the <strong>{form.pickup_window}</strong> window. Our team
            will call <strong>{form.phone}</strong> to confirm details.
          </p>
          <Button
            data-testid="booking-reset-btn"
            onClick={() => {
              setSuccess(false);
              setForm({
                name: "",
                phone: "",
                email: "",
                address: "",
                city: BUSINESS.serviceAreas[0],
                services: ["Dry Cleaning"],
                pickup_date: null,
                pickup_window: "",
                notes: "",
              });
            }}
            className="mt-8 rounded-full bg-brand-navy hover:bg-brand-blue text-white h-11 px-6"
          >
            Schedule another pickup
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="schedule"
      data-testid="booking-section"
      className="relative py-20 lg:py-28 bg-slate-50 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
        <img src={FABRIC_BG} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">
            Free pickup & delivery
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy">
            Schedule your pickup in under a minute.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Tell us a few details and we'll be at your door — free across
            Chandler, Maricopa, Ahwatukee, Tempe and Gilbert.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Free pickup & delivery, no minimums",
              "We text you 30 min before arrival",
              "Pay only after we weigh / inspect",
              "100% satisfaction guarantee",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700">
                <ShieldCheck className="h-5 w-5 text-brand-blue mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="lg:col-span-7 rounded-3xl bg-white border border-slate-200 shadow-xl p-7 lg:p-10"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="bk-name" className="text-brand-navy font-semibold">
                Full name
              </Label>
              <Input
                id="bk-name"
                data-testid="booking-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Jane Doe"
                className="mt-2 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="bk-phone" className="text-brand-navy font-semibold">
                Phone
              </Label>
              <Input
                id="bk-phone"
                data-testid="booking-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="(480) 555-1234"
                className="mt-2 h-11"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bk-email" className="text-brand-navy font-semibold">
                Email <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="bk-email"
                data-testid="booking-input-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bk-address" className="text-brand-navy font-semibold">
                Pickup address
              </Label>
              <Input
                id="bk-address"
                data-testid="booking-input-address"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                placeholder="123 Main St, Apt 4"
                className="mt-2 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-brand-navy font-semibold">City</Label>
              <Select value={form.city} onValueChange={(v) => update("city", v)}>
                <SelectTrigger
                  data-testid="booking-select-city"
                  className="mt-2 h-11"
                >
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS.serviceAreas.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}, AZ
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-brand-navy font-semibold">
                Pickup date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="booking-date-trigger"
                    className="mt-2 h-11 w-full justify-start text-left font-normal border-slate-200"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-brand-blue" />
                    {form.pickup_date
                      ? format(form.pickup_date, "EEE, MMM d")
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.pickup_date}
                    onSelect={(d) => update("pickup_date", d)}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (d < today) return true;
                      return !isDayOpen(form.city, d);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {form.city && (
                <p className="mt-2 text-xs text-slate-500" data-testid="booking-schedule-hint">
                  {isMaricopa(form.city)
                    ? "Maricopa: pickups Mon & Thu, 7–10 AM or 3–6 PM. Returns delivered after 3 PM, 48 hrs later. No Saturday service."
                    : `${form.city}: pickups Tue / Wed / Thu. Saturdays 10 AM–2 PM. 48-hour turnaround.`}
                </p>
              )}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-brand-navy font-semibold">Time window</Label>
              <Select
                value={form.pickup_window}
                onValueChange={(v) => update("pickup_window", v)}
                disabled={!form.pickup_date || availableWindows.length === 0}
              >
                <SelectTrigger
                  data-testid="booking-select-window"
                  className="mt-2 h-11"
                >
                  <SelectValue placeholder={windowPlaceholder(!!form.pickup_date, availableWindows.length > 0)} />
                </SelectTrigger>
                <SelectContent>
                  {availableWindows.map((w) => {
                    const info = availability[w];
                    const remaining = info?.remaining;
                    const full = info?.full;
                    return (
                      <SelectItem key={w} value={w} disabled={full}>
                        <span className="flex items-center justify-between gap-3 w-full">
                          <span>{w}</span>
                          {info && (
                            <span className={`text-xs ${capacityColor(full, remaining)}`}>
                              {full ? "Full" : `${remaining} of ${MAX_PER_SLOT} left`}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-7">
            <Label className="text-brand-navy font-semibold">Services</Label>
            <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {SERVICES.map((s) => {
                const checked = form.services.includes(s);
                return (
                  <label
                    key={s}
                    data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex items-center gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${
                      checked
                        ? "border-brand-blue bg-brand-lightblue/40"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleService(s)}
                    />
                    <span className="text-sm font-medium text-brand-navy">
                      {s}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-7">
            <Label htmlFor="bk-notes" className="text-brand-navy font-semibold">
              Special instructions{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="bk-notes"
              data-testid="booking-input-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Gate code, fabric concerns, starch preferences…"
              className="mt-2 min-h-[88px]"
            />
          </div>

          <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="booking-payment-notice">
            <span className="text-amber-700 text-lg leading-none mt-0.5">💳</span>
            <div className="text-sm text-slate-700 leading-relaxed">
              <p className="font-semibold text-brand-navy">Payment</p>
              <p className="mt-1">
                We'll need your credit card information at pickup. You won't be charged until we've checked your items and entered them into our system — you'll only pay for what we actually clean.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="booking-submit-btn"
            disabled={loading}
            className="mt-8 w-full sm:w-auto rounded-full bg-brand-blue hover:bg-brand-navy text-white h-12 px-8 font-semibold text-base"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              "Reserve free pickup"
            )}
          </Button>
          <p className="mt-4 text-xs text-slate-500">
            By submitting, you agree we may text or call to confirm details.
            No spam — ever.
          </p>
        </form>
      </div>
    </section>
  );
}
